import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { CalendarCheck2, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Club, Court, Reservation } from "@/types";
import { useAuthStore } from "@/store/auth";
import { useSocketStatus } from "@/hooks/useSocketStatus";
import { Card, Badge } from "@/components/ui/Card";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { RefereeCodeSection } from "@/components/RefereeCodeSection";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";

function LiveIndicator() {
  const connected = useSocketStatus();
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-ink-50 px-2.5 py-1 text-xs font-medium text-ink-500">
      <span className={`h-1.5 w-1.5 rounded-full ${connected ? "animate-pulse bg-brand-500" : "bg-ink-300"}`} />
      {connected ? "En vivo" : "Reconectando…"}
    </span>
  );
}

const dateFormatter = new Intl.DateTimeFormat("es-ES", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function CourtCell({ reservation, courtsByClub, editable }: { reservation: Reservation; courtsByClub: Map<number, Court[]>; editable: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const options = courtsByClub.get(reservation.clubId) ?? [];

  const assign = useMutation({
    mutationFn: (courtId: number | null) => api.put(`/reservations/${reservation.id}`, { courtId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      setEditing(false);
      toast.success("Pista actualizada");
    },
    onError: () => {
      toast.error("No se pudo actualizar la pista");
      setEditing(false);
    },
  });

  if (editable && editing) {
    return (
      <select
        autoFocus
        defaultValue={reservation.courtId ?? ""}
        onBlur={() => setEditing(false)}
        onChange={(e) => assign.mutate(e.target.value ? Number(e.target.value) : null)}
        className="rounded-lg border border-ink-100 bg-white px-2 py-1 text-xs outline-none focus:border-brand-500"
      >
        <option value="">Sin asignar</option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <button
      type="button"
      onClick={() => editable && setEditing(true)}
      className={`text-left ${editable ? "cursor-pointer" : "cursor-default"}`}
      title={editable ? "Cambiar la pista asignada" : undefined}
    >
      {reservation.court ? (
        <span className="text-ink-600">{reservation.court.name}</span>
      ) : (
        <Badge tone="red">Sin asignar</Badge>
      )}
      <span className="block text-xs text-ink-300">{reservation.courtLabel}</span>
    </button>
  );
}

function BlockCourtModal({ open, onOpenChange, courtsByClub }: { open: boolean; onOpenChange: (open: boolean) => void; courtsByClub: Map<number, Court[]> }) {
  const queryClient = useQueryClient();
  const allCourts = useMemo(() => [...courtsByClub.values()].flat(), [courtsByClub]);
  const [courtId, setCourtId] = useState<number | "">("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [motivo, setMotivo] = useState("");
  const [confirming, setConfirming] = useState(false);

  const block = useMutation({
    mutationFn: () => api.post("/reservations/block", { courtId: Number(courtId), startsAt, endsAt, motivo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      toast.success("Pista bloqueada");
      setConfirming(false);
      onOpenChange(false);
      setCourtId("");
      setStartsAt("");
      setEndsAt("");
      setMotivo("");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error ?? "No se pudo bloquear la pista");
      setConfirming(false);
    },
  });

  const selectedCourtName = allCourts.find((c) => c.id === courtId)?.name ?? "";

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Bloquear pista" description="Para mantenimiento, un evento privado... no es una reserva de cliente.">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (courtId && startsAt && endsAt && motivo.trim()) setConfirming(true);
        }}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-600">Pista</label>
          <select
            value={courtId}
            onChange={(e) => setCourtId(e.target.value ? Number(e.target.value) : "")}
            required
            className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          >
            <option value="">Selecciona una pista…</option>
            {allCourts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input type="datetime-local" label="Desde" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required />
          <Input type="datetime-local" label="Hasta" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} required />
        </div>
        <Input label="Motivo" placeholder="Mantenimiento, evento privado…" value={motivo} onChange={(e) => setMotivo(e.target.value)} required />
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" disabled={block.isPending}>
            Bloquear pista
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Bloquear pista"
        description={`¿Seguro que quieres bloquear "${selectedCourtName}" en ese horario? Dejará de estar disponible para reservas de clientes.`}
        tone="primary"
        confirmLabel="Bloquear pista"
        onConfirm={() => block.mutate()}
      />
    </Modal>
  );
}

interface ReservationFilters {
  clubId: string;
  pista: string;
  status: string;
  customerName: string;
  desde: string;
  hasta: string;
}

const emptyReservationFilters: ReservationFilters = {
  clubId: "",
  pista: "",
  status: "",
  customerName: "",
  desde: "",
  hasta: "",
};

interface PaginatedReservations {
  data: Reservation[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export function ReservationsPage() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const canManageCourts =
    user?.role === "SUPER_ADMIN" || user?.role === "PROVIDER" || (user?.role === "CLUB" && user.permissions.includes("MANAGE_COURTS"));
  const ownedClubs = useMemo(() => user?.ownedClubs ?? [], [user?.ownedClubs]);
  const [filters, setFilters] = useState<ReservationFilters>(emptyReservationFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [unblocking, setUnblocking] = useState<Reservation | null>(null);
  const queryClient = useQueryClient();

  function updateFilter<K extends keyof ReservationFilters>(key: K, value: ReservationFilters[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }

  const unblock = useMutation({
    mutationFn: (id: number) => api.delete(`/reservations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      toast.success("Bloqueo quitado");
    },
    onError: () => toast.error("No se pudo quitar el bloqueo"),
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters.clubId) params.set("clubId", filters.clubId);
    if (filters.pista) params.set("pista", filters.pista);
    if (filters.status) params.set("status", filters.status);
    if (filters.customerName) params.set("customerName", filters.customerName);
    if (filters.desde) params.set("desde", filters.desde);
    if (filters.hasta) params.set("hasta", filters.hasta);
    return params.toString();
  }, [page, limit, filters]);

  const { data: reservationsResp, isLoading } = useQuery({
    queryKey: ["reservations", queryString],
    queryFn: async () => (await api.get<PaginatedReservations>(`/reservations?${queryString}`)).data,
    placeholderData: keepPreviousData,
  });
  const reservations = reservationsResp?.data ?? [];

  const { data: clubs } = useQuery({
    queryKey: ["clubs"],
    queryFn: async () => (await api.get<{ data: Club[] }>("/clubs?limit=100")).data.data,
    enabled: isSuperAdmin,
  });

  const { data: courts } = useQuery({
    queryKey: ["courts"],
    queryFn: async () => (await api.get<Court[]>("/courts")).data,
    enabled: canManageCourts,
  });

  const courtsByClub = useMemo(() => {
    const map = new Map<number, Court[]>();
    for (const c of courts ?? []) {
      if (!c.active) continue;
      map.set(c.clubId, [...(map.get(c.clubId) ?? []), c]);
    }
    return map;
  }, [courts]);

  const clubFilterOptions = isSuperAdmin ? (clubs ?? []) : ownedClubs.length > 1 ? ownedClubs : [];
  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Partidas" }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
            <CalendarCheck2 className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Partidas</h1>
            <p className="text-ink-400">
              {isSuperAdmin ? "Partidas de todos los clubes, en tiempo real." : "Partidas de tu club, en tiempo real."}
            </p>
          </div>
        </div>
        <LiveIndicator />
      </div>

      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {clubFilterOptions.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Club</label>
              <select
                value={filters.clubId}
                onChange={(e) => updateFilter("clubId", e.target.value)}
                className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              >
                <option value="">Todos</option>
                {clubFilterOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Input
            label="Pista"
            placeholder="Nombre de la pista…"
            value={filters.pista}
            onChange={(e) => updateFilter("pista", e.target.value)}
            className="w-full"
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-600">Estado</label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
              className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="">Todos</option>
              <option value="CONFIRMED">Confirmada</option>
              <option value="CANCELLED">Cancelada</option>
              <option value="BLOQUEO">Bloqueo</option>
            </select>
          </div>
          <Input
            label="Cliente"
            placeholder="Buscar cliente…"
            value={filters.customerName}
            onChange={(e) => updateFilter("customerName", e.target.value)}
            className="w-full"
          />
          <Input type="date" label="Desde" value={filters.desde} onChange={(e) => updateFilter("desde", e.target.value)} className="w-full" />
          <Input type="date" label="Hasta" value={filters.hasta} onChange={(e) => updateFilter("hasta", e.target.value)} className="w-full" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {hasActiveFilters ? (
            <Button
              onClick={() => {
                setFilters(emptyReservationFilters);
                setPage(1);
              }}
            >
              <X className="h-4 w-4" />
              Limpiar filtros
            </Button>
          ) : (
            <span />
          )}
          {canManageCourts && (
            <Button onClick={() => setBlockModalOpen(true)}>
              <Lock className="h-4 w-4" />
              Bloquear pista
            </Button>
          )}
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <TableSkeleton rows={5} cols={5} />
        ) : !reservations.length ? (
          <p className="p-6 text-sm text-ink-400">
            {hasActiveFilters ? "Ninguna partida coincide con los filtros." : "Todavía no ha llegado ninguna reserva."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                <tr>
                  <th className="px-6 py-3 font-semibold">Fecha</th>
                  <th className="px-6 py-3 font-semibold">Cliente</th>
                  <th className="px-6 py-3 font-semibold">Pista</th>
                  {isSuperAdmin && <th className="px-6 py-3 font-semibold">Club</th>}
                  <th className="px-6 py-3 font-semibold">Estado</th>
                  <th className="px-6 py-3 font-semibold">Preparar partido</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id} className="border-t border-ink-100 transition-colors hover:bg-ink-50/50">
                    <td className="px-6 py-3.5 text-ink-600">
                      {dateFormatter.format(new Date(r.startsAt))}
                    </td>
                    <td className="px-6 py-3.5">
                      <p className="flex items-center gap-1.5 font-medium text-ink-800">
                        {r.tipo === "BLOQUEO" && <Lock className="h-3.5 w-3.5 text-ink-400" />}
                        {r.customerName}
                      </p>
                      {r.customerPhone && <p className="text-xs text-ink-400">{r.customerPhone}</p>}
                    </td>
                    <td className="px-6 py-3.5 text-ink-500">
                      <CourtCell reservation={r} courtsByClub={courtsByClub} editable={canManageCourts} />
                    </td>
                    {isSuperAdmin && <td className="px-6 py-3.5 text-ink-500">{r.club.nombre}</td>}
                    <td className="px-6 py-3.5">
                      {r.tipo === "BLOQUEO" ? (
                        <Badge tone="slate">Bloqueo</Badge>
                      ) : (
                        <Badge tone={r.status === "CONFIRMED" ? "green" : "red"}>
                          {r.status === "CONFIRMED" ? "Confirmada" : "Cancelada"}
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-3.5">
                      {r.tipo === "BLOQUEO" ? (
                        <button
                          onClick={() => setUnblocking(r)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline"
                        >
                          <X className="h-3.5 w-3.5" />
                          Quitar bloqueo
                        </button>
                      ) : r.status === "CONFIRMED" && !r.matchEnded ? (
                        <div className="max-w-[220px]">
                          <RefereeCodeSection reservationId={r.id} refereeCode={r.refereeCode} queryKey={["reservations"]} />
                        </div>
                      ) : (
                        <span className="text-xs text-ink-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Pagination
        page={reservationsResp?.page ?? page}
        totalPages={reservationsResp?.totalPages ?? 1}
        total={reservationsResp?.total ?? 0}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      <BlockCourtModal open={blockModalOpen} onOpenChange={setBlockModalOpen} courtsByClub={courtsByClub} />

      <ConfirmDialog
        open={!!unblocking}
        onOpenChange={(open) => !open && setUnblocking(null)}
        title="¿Quitar este bloqueo?"
        description={`Se quitará el bloqueo "${unblocking?.customerName}" y la pista volverá a estar disponible en ese horario.`}
        tone="danger"
        confirmLabel="Quitar bloqueo"
        onConfirm={() => unblocking && unblock.mutate(unblocking.id)}
      />
    </div>
  );
}
